// Canonical vocabulary of the public site's editable copy — ONE key list, ONE resolver.
//
// A tenant edits its public site from Admin -> Site Content (ContentAdmin), which stores
// rows in `content_blocks` (POST /api/admin/content) and serves them back to every public
// page through GET /api/content -> LanguageContext `overrides`. That pipeline only pays off
// if the writer and the readers agree on the key spelling, and they did not: ContentAdmin
// wrote dotted keys (`story.lead`, `contact.email`, ...) that NO renderer read, while
// SiteBuilderWizard wrote a second, undotted vocabulary (`hero_title`, `hero_subtitle`,
// `services_intro`) that only the t2..t6 templates read. Saved copy therefore never reached
// the Classic site and only partially reached the themes.
//
// The dotted keys are the canonical vocabulary (they name the dictionary path they replace,
// and they are what the admin UI exposes). Everything that renders public copy — the Classic
// components in components/site/ and the five templates via templates/shared/useSiteData.js —
// resolves it through `resolveSiteCopy` below, reached from the ONE hook that holds all of its
// inputs (`lib/useSiteCopy.js`), so there is exactly one place where an override is layered
// over a dictionary default and exactly one contact-e-mail precedence.
import { ASSETS, SITE_IMAGE_SLOTS, translations } from "./content";

/**
 * The editable text slots: `key` = `content_blocks` key, `path` = the dictionary path in
 * `translations[lang]` (lib/content.js) it replaces when an override exists.
 *
 * @property hint       the editor shows `content.hint.<key>` under the row's label — for a
 *                      row whose behaviour a label alone cannot explain (line breaks,
 *                      "empty hides it")
 * @property emptyHides a saved row with no text in ANY language hides the slot instead of
 *                      falling back to the sample. Only for slots that are optional
 *                      decoration (the handwritten phrase, the subtitle): every other row
 *                      keeps "empty = the sample", which is what stops one empty save from
 *                      blanking a heading. "Any language", not "this language": the editor
 *                      shows one language tab at a time, so an empty column usually means
 *                      "not translated yet", and that must show the sample, as everywhere
 *                      else. Deleting the row brings the sample back.
 *
 * The hero badge, the three stats and the credential default to "" in the dictionary
 * (BUG-210: they state facts about the practitioner that no sample may invent), so an
 * unset one renders nothing and needs no flag. The editor's grouping of these keys is
 * CONTENT_SECTIONS below; a test pins that every key here has exactly one place there.
 */
export const SITE_TEXT_OVERRIDES = [
  { key: "hero.eyebrow", path: "hero.eyebrow", hint: true },
  // ONE field for the whole headline, a line per Enter. It used to be three keys
  // (titleA/B/C): saving the first left the sample's other two lines on the site, and a
  // line could not be removed at all. backend/migration.py folds old rows into this key.
  { key: "hero.title", path: "hero.title", hint: true },
  { key: "hero.script", path: "hero.script", hint: true, emptyHides: true },
  { key: "hero.sub", path: "hero.sub", hint: true, emptyHides: true },
  { key: "hero.stat1", path: "hero.stat1" },
  { key: "hero.stat1Label", path: "hero.stat1Label" },
  { key: "hero.stat2", path: "hero.stat2" },
  { key: "hero.stat2Label", path: "hero.stat2Label" },
  { key: "hero.stat3", path: "hero.stat3" },
  { key: "hero.stat3Label", path: "hero.stat3Label" },
  { key: "story.eyebrow", path: "story.eyebrow" },
  { key: "story.title", path: "story.title" },
  { key: "story.lead", path: "story.lead" },
  { key: "story.quote", path: "story.quote" },
  { key: "approach.eyebrow", path: "approach.eyebrow" },
  { key: "approach.title", path: "approach.title" },
  { key: "approach.lead", path: "approach.lead" },
  { key: "services.lead", path: "services.lead" },
  { key: "contact.lead", path: "contact.lead" },
  { key: "contact.email", path: "contact.info.email" },
  { key: "contact.place", path: "contact.info.place" },
];

const EMPTY_HIDES = new Set(SITE_TEXT_OVERRIDES.filter((o) => o.emptyHides).map((o) => o.key));
const OVERRIDE_PATH = Object.fromEntries(SITE_TEXT_OVERRIDES.map((o) => [o.key, o.path]));

/**
 * The text the public site shows for `key` while nothing is saved, in the language a
 * `content_blocks` column serves: "fr" -> value_fr, anything else -> value_en (which is also
 * what ES visitors fall back to). The editor shows it as the placeholder of an empty field,
 * so "empty" never hides what the visitor actually reads. "" for a key with no sample.
 */
export function sampleTextFor(key, lang) {
  const path = OVERRIDE_PATH[key];
  if (!path) return "";
  const value = path.split(".").reduce((o, k) => o?.[k], translations[lang === "fr" ? "fr" : "en"]);
  return typeof value === "string" ? value : "";
}

/**
 * THE rule for an `emptyHides` slot being hidden on purpose: its row exists and is blank in
 * every language. Shared by the public resolver (lib/useSiteCopy.js) and the editor's
 * "hidden on your site" badge, so the two can never disagree.
 *
 * @param {object} [row] the saved `content_blocks` row ({value_fr, value_en}), if any
 */
export function isHiddenRow(key, row) {
  return EMPTY_HIDES.has(key) && Boolean(row) && !hasText(row.value_fr) && !hasText(row.value_en);
}

/** Same list, keys only — what the admin editor renders a row for. */
export const SITE_TEXT_OVERRIDE_KEYS = SITE_TEXT_OVERRIDES.map((o) => o.key);

/**
 * Image-slot keys (`site.heroBg`, `site.logo`, …) — stored in `content_blocks` by the
 * SiteImagesAdmin but owned by that screen, not by the text editor.  The content editor
 * must exclude them from its "custom blocks" list so raw media URLs never leak into the
 * text-editing UI.
 */
export const SITE_IMAGE_SLOT_KEYS = new Set(SITE_IMAGE_SLOTS.map((s) => s.key));

// ---------------------------------------------------------------------------
// Repeating sections (BUG-025)
//
// `story.chapters` and `approach.pillars` are LISTS in the dictionary, and a list has no
// single key to override — so the sections above were editable while the five chapters and
// four pillars they introduce stayed bundled sample text forever.
//
// A list is stored as one `content_blocks` row per FIELD of one ITEM, keyed
// `story.chapters.<index>.<field>`. Every value stays a scalar string, so it keeps the two
// language columns and the strict single-language discipline documented below — which a
// JSON blob in `value_fr` would have destroyed (one column for both languages, no per-field
// diff, no per-field delete). No new collection, no new endpoint, no new vocabulary: an
// item key is an ordinary content key that the existing POST/DELETE /admin/content and
// GET /content already carry.
//
// The list is therefore COMPOSED at read time, and the index is storage, not identity:
// removing an item rewrites the indices of the ones after it (admin side,
// `buildRepeaterWrites`) and the resolver compacts whatever holes remain.
// ---------------------------------------------------------------------------

/**
 * The repeating sections of the public site.
 *
 * @property key      the `content_blocks` key PREFIX; an item field is `${key}.${i}.${name}`
 * @property path     the dictionary path (translations[lang]) holding the sample list this
 *                    replaces — served verbatim while the tenant has saved nothing
 * @property max      hard cap on items; the resolver scans indices [0, max)
 * @property numbered items get a derived, 1-based `n` ("01", "02", …) — Story prints it
 * @property fields   the editable fields of one item, in editor order; `multiline` picks
 *                    the input widget
 */
export const SITE_REPEATERS = [
  {
    key: "story.chapters",
    path: "story.chapters",
    max: 8,
    numbered: true,
    fields: [
      { name: "title", multiline: false },
      { name: "body", multiline: true },
    ],
  },
  {
    key: "approach.pillars",
    path: "approach.pillars",
    max: 6,
    numbered: false,
    fields: [
      { name: "title", multiline: false },
      { name: "body", multiline: true },
    ],
  },
];

const REPEATER_BY_KEY = Object.fromEntries(SITE_REPEATERS.map((r) => [r.key, r]));

/**
 * Admin-UI grouping: preset text keys and repeaters ordered by the section they belong to
 * on the live site.  Each entry names its section, lists the text keys that belong in it,
 * and optionally points to the repeater that follows them (if the section has one).
 *
 * ContentAdmin uses this to render keys and repeaters interleaved — "My journey" fields
 * first, then the chapters editor, then "Approach" fields, then the pillars editor — so the
 * practitioner sees the same top-to-bottom order as their visitors.  The old layout dumped
 * ALL text keys first and ALL repeaters at the bottom, placing the chapters editor below
 * "Contact" instead of below "My journey".
 *
 * i18n label key: `content.section.${section}`  (rendered as a divider heading).
 */
export const CONTENT_SECTIONS = [
  {
    section: "hero",
    keys: [
      "hero.eyebrow", "hero.title", "hero.script", "hero.sub",
      "hero.stat1", "hero.stat1Label", "hero.stat2", "hero.stat2Label", "hero.stat3", "hero.stat3Label",
    ],
    repeater: null,
  },
  { section: "story",    keys: ["story.eyebrow", "story.title", "story.lead", "story.quote"],                       repeater: REPEATER_BY_KEY["story.chapters"] },
  { section: "approach", keys: ["approach.eyebrow", "approach.title", "approach.lead"],                              repeater: REPEATER_BY_KEY["approach.pillars"] },
  { section: "services", keys: ["services.lead"],                                                                    repeater: null },
  { section: "contact",  keys: ["contact.lead", "contact.email", "contact.place"],                                   repeater: null },
];

/** The ONE place an item's key is spelled. */
export const repeaterFieldKey = (repeater, index, field) => `${repeater.key}.${index}.${field}`;

// `story.chapters.<index>.<declared field>` — no leading zero on the index, so one item
// index has exactly one spelling and "…007.title" can never shadow "…7.title".
const REPEATER_PATTERNS = SITE_REPEATERS.map((r) => [
  r,
  new RegExp(`^${r.key.replace(/\./g, "\\.")}\\.(0|[1-9]\\d*)\\.(${r.fields.map((f) => f.name).join("|")})$`),
]);

/** `{repeater, index, field}` for an item key, `null` for anything else. */
const parseRepeaterKey = (key) => {
  for (const [repeater, re] of REPEATER_PATTERNS) {
    const m = re.exec(key || "");
    if (m) return { repeater, index: Number(m[1]), field: m[2] };
  }
  return null;
};

/**
 * Is this key owned by a repeating section? Such a key is edited ONLY by its section, so
 * the generic block editor must not list it as a custom block (two editors for one row,
 * one of which does not know about indices, is how a list gets shredded).
 * True past `max` as well: that row is an orphan the next save sweeps, not a custom block.
 */
export const isRepeaterKey = (key) => parseRepeaterKey(key) !== null;

/**
 * A field carries content when it is not blank — whitespace is not copy. Exported so
 * renderers can apply the same "unset means hidden, not filler" guard the sample-text
 * paths above use internally (BUG-210: Hero.jsx's stats/credential/eyebrow,
 * templates/shared/useSiteData.js's `stats`, and Footer.jsx's tagline).
 */
export const hasText = (v) => typeof v === "string" && v.trim() !== "";

/** "01", "02", … — the chapter number is the RENDERED position, never stored. */
const orderNumber = (index) => String(index + 1).padStart(2, "0");

/**
 * Compose one repeating section for the CURRENT language.
 *
 * @param {object} repeater  one of SITE_REPEATERS
 * @param {Array}  dictItems the dictionary's sample list (may be undefined)
 * @param {(key: string) => string} read  single-language override read, "" when unset
 *
 * An item EXISTS as soon as any of its fields is non-empty in this language; its other
 * fields stay empty rather than borrowing from the sample list (item 3 of the samples has
 * nothing to do with item 3 of a practitioner's story). Zero items → the sample list
 * verbatim, which is what keeps first-run guidance on a brand-new site.
 */
export function resolveRepeaterItems(repeater, dictItems, read) {
  const items = [];
  for (let i = 0; i < repeater.max; i++) {
    const item = {};
    let present = false;
    for (const f of repeater.fields) {
      const value = read(repeaterFieldKey(repeater, i, f.name)) || "";
      if (hasText(value)) present = true;
      item[f.name] = value;
    }
    if (present) items.push(item);
  }
  if (items.length === 0) return Array.isArray(dictItems) ? dictItems : [];
  return repeater.numbered ? items.map((it, i) => ({ n: orderNumber(i), ...it })) : items;
}

// The admin-side write plan for a repeating section (`readRepeaterItems` /
// `buildRepeaterWrites`) lives further down, after the single-language helpers whose
// discipline it inherits.

// ---------------------------------------------------------------------------
// Single-language access to a `content_blocks` row.
//
// A row stores two columns (value_fr, value_en) and `override()` serves them with NO
// cross-language fallback for the two authored languages: an FR-only row leaves EN
// visitors on the English dictionary default rather than showing them French. Any admin
// UI that has ONE input per slot (the site builder wizard) must obey the same discipline,
// and for one reason: it reads a slot to prefill a field and later writes that field back.
// Read one language, write another, and the round-trip PUBLISHES the wrong language —
// `pickLocalized`'s EN chain (["_en","_fr"]) would put French text in the field of an
// English admin session, and saving would then stamp it into value_en, where the public
// resolver has no fallback to protect the visitor. So: read exactly the column that will
// be written, and never copy text from a language into another that already has text.
// ---------------------------------------------------------------------------

/**
 * The `content_blocks` column an admin session in `lang` reads AND writes.
 * The collection stores only FR and EN, so every non-FR session works on value_en — which
 * is also the column `override()` serves to ES visitors (their chain is es -> en -> fr).
 */
export const contentColumnFor = (lang) => (lang === "fr" ? "value_fr" : "value_en");

/** The other column — the one an admin session in `lang` must never overwrite blindly. */
const otherContentColumn = (lang) => (lang === "fr" ? "value_en" : "value_fr");

/**
 * STRICT read of a `content_blocks` row for `lang`: no cross-language fallback, ever.
 * Empty means "this language has nothing saved" — the caller leaves its field empty and
 * writes nothing, which is what keeps an untranslated slot untranslated.
 */
export function readContentValue(block, lang) {
  return (block && block[contentColumnFor(lang)]) || "";
}

/**
 * Upsert body for POST /api/admin/content from a single-input admin UI.
 * Writes the current language's column; the other language keeps its saved text, and only
 * an EMPTY other column is filled with the same text (nothing is destroyed, and a
 * unilingual practitioner's own words beat the bundled sample copy).
 */
export function contentUpsertPayload({ key, value, existing, lang }) {
  const other = otherContentColumn(lang);
  return {
    key,
    [contentColumnFor(lang)]: value,
    [other]: (existing && existing[other]) || value,
  };
}

/**
 * The items an admin session in `lang` edits: strictly this language's column, no
 * dictionary fallback (the editor edits SAVED rows — showing the samples in the inputs
 * would let one "save" publish the sample text as the tenant's own words).
 *
 * @param {object} byKey saved rows keyed by content key ({key: {value_fr, value_en}})
 * @returns {Array<object>} `[{title, body}, …]`, compacted, in index order
 */
export function readRepeaterItems(repeater, byKey, lang) {
  const items = resolveRepeaterItems(repeater, [], (key) =>
    readContentValue((byKey || {})[key], lang),
  );
  // Strip the derived `n`: it is a rendering detail, and nothing editable maps to it.
  return items.map((it) => Object.fromEntries(repeater.fields.map((f) => [f.name, it[f.name] || ""])));
}

/**
 * The writes that make the saved rows equal `items`, for an admin session in `lang`.
 *
 * @returns {{upserts: Array<object>, deletes: Array<string>}} POST bodies for
 *   /admin/content, then keys to DELETE /admin/content/{key}.
 *
 * Three rules, each one a way a list silently corrupts itself without it:
 *   - fully blank items are dropped, so what is saved is what the site renders;
 *   - EVERY field of a kept item is written, including an emptied one — skipping it would
 *     leave the previous text in place, and clearing a field would appear not to work;
 *   - a vacated index is DELETED, otherwise the removed item simply comes back on the next
 *     page load. Unless the row still holds the OTHER language: then it is CLEARED for this
 *     language only, because an English session must never erase French copy.
 *
 * Known and accepted: the index is per-row, not per-language, so reordering in one language
 * reorders only that language's column (the pairing of a title with its body is preserved;
 * the ORDER of an untranslated list is not). The alternative — rewriting both columns from
 * one session — is the clobber `contentUpsertPayload` exists to prevent.
 */
export function buildRepeaterWrites({ repeater, items, byKey, lang }) {
  const rows = byKey || {};
  const kept = (items || []).filter((it) => repeater.fields.some((f) => hasText(it?.[f.name])));

  const upserts = [];
  kept.forEach((item, i) => {
    for (const f of repeater.fields) {
      const key = repeaterFieldKey(repeater, i, f.name);
      upserts.push(contentUpsertPayload({ key, value: item?.[f.name] || "", existing: rows[key], lang }));
    }
  });

  // Every row of THIS repeater at or past the new length — including orphans past `max`,
  // which the resolver ignores and would otherwise never be cleaned up.
  const fieldOrder = (name) => repeater.fields.findIndex((f) => f.name === name);
  const vacated = Object.keys(rows)
    .map((key) => ({ key, ...(parseRepeaterKey(key) || {}) }))
    .filter((p) => p.repeater?.key === repeater.key && p.index >= kept.length)
    .sort((a, b) => a.index - b.index || fieldOrder(a.field) - fieldOrder(b.field));

  const other = otherContentColumn(lang);
  const deletes = [];
  for (const { key } of vacated) {
    if (hasText(rows[key]?.[other])) {
      upserts.push(contentUpsertPayload({ key, value: "", existing: rows[key], lang }));
    } else {
      deletes.push(key);
    }
  }
  return { upserts, deletes };
}

/**
 * The three canonical slots the site builder wizard's content step edits, and the local
 * field name each one is bound to. The wizard used to write a private vocabulary
 * (hero_title / hero_subtitle / services_intro) that only the t2..t6 templates honoured
 * and Admin -> Site Content could not reach.
 */
export const WIZARD_CONTENT_FIELDS = [
  { field: "heroTitle", key: "hero.title" },
  { field: "heroSub", key: "hero.sub" },
  { field: "servicesLead", key: "services.lead" },
];

/**
 * Prefill of the wizard's content step.
 *
 * @param {object} byKey   saved rows keyed by content key ({key: {value_fr, value_en}})
 * @param {object} tenant  tenant record (/tenant/self/info)
 * @param {string} lang    the admin's current language
 *
 * A slot that HAS a row is prefilled strictly from this language's column — empty when this
 * language has nothing, so the auto-save on the Publish step writes nothing and cannot
 * clobber the other language. A slot with NO row at all is a first-run suggestion from the
 * tenant record, which can only fill columns that are empty in every language.
 */
export function buildContentPrefill(byKey, tenant, lang) {
  const suggestions = {
    "hero.title": tenant?.name || "",
    "hero.sub": tenant?.branding?.tagline || "",
    "services.lead": "",
  };
  const prefill = {};
  for (const { field, key } of WIZARD_CONTENT_FIELDS) {
    const block = (byKey || {})[key];
    prefill[field] = block ? readContentValue(block, lang) : suggestions[key];
  }
  return prefill;
}

/**
 * The upserts the wizard must POST for its content step — an empty field is not a write
 * (that is what makes "this language has nothing saved" a no-op instead of a clobber).
 */
export function buildContentUpserts(content, byKey, lang) {
  const upserts = [];
  for (const { field, key } of WIZARD_CONTENT_FIELDS) {
    const value = (content || {})[field];
    if (!value) continue;
    upserts.push(contentUpsertPayload({ key, value, existing: (byKey || {})[key], lang }));
  }
  return upserts;
}

/**
 * Layer the tenant's `content_blocks` overrides over the i18n dictionary.
 *
 * @param {object} t         resolved dictionary for the current language (LanguageContext `t`)
 * @param {(key: string) => string} override  returns the override for `key` in the current
 *   language, or "" when there is none — an empty override is "unset", never "blank the site".
 * @param {object} [tenant]  the tenant record being served (TenantContext). It supplies the
 *   MIDDLE rank of the contact e-mail — saved override, else `business_email`/`owner_email`,
 *   else the bundled sample — so a practice that filled in Settings and never opened Site
 *   Content still publishes a real inbox. Omitted (marketing/preview, or before the record
 *   resolves) the chain degrades to the sample. `lib/useSiteCopy.js` is the hook that always
 *   passes it; call this function directly only where there is genuinely no tenant.
 * @param {(key: string) => boolean} [isCleared]  whether a `content_blocks` row EXISTS for
 *   `key` and holds no text in any language — emptied on purpose. Only the `emptyHides`
 *   slots read it: for them a cleared row means "hidden", which `override` alone cannot tell
 *   apart from "never saved" or "not translated" (all three are ""). Omitted, nothing counts
 *   as cleared — the slots show their sample, never an unexplained blank.
 * @returns {object} `t` with the overridable slots resolved: same shape as `t` plus
 *   `hero.titleLines` (the headline as an array, a line per line break, blank lines
 *   dropped) and `images`
 *   (the `site.*` slots resolved against their bundled fallback).
 *
 * Every read is optional-chained: a partially translated locale (translations.es) or a bare
 * dictionary must degrade to a missing string, never to a white screen on a public site.
 */
export function resolveSiteCopy(t, override, tenant, isCleared) {
  const read = (key) => (typeof override === "function" ? override(key) : "") || "";
  // Whitespace is never text: an override of only spaces/line breaks is unset, on every row.
  const ox = (key, fallback) => (hasText(read(key)) ? read(key) : fallback || "");
  // An `emptyHides` slot: a row emptied in every language is the practitioner's answer.
  const optional = (key, fallback) => {
    if (!EMPTY_HIDES.has(key) || hasText(read(key))) return ox(key, fallback);
    return typeof isCleared === "function" && isCleared(key) ? "" : fallback || "";
  };
  // The sample list a repeating section falls back to, read from its declared path.
  const repeated = (repeater, dict, read) =>
    resolveRepeaterItems(repeater, repeater.path.split(".").reduce((o, k) => o?.[k], dict), (key) => read(key));
  // A saved title is the WHOLE headline; the sample's lines never complete it. Whitespace
  // is not a title: it would render an empty heading, so it counts as unset.
  const title = hasText(read("hero.title")) ? read("hero.title") : t?.hero?.title || "";
  // The three hero figures. A label is the caption OF a value: with no value it would
  // stand alone ("ONLINE" under nothing), so it resolves to "" too. A value alone is fine.
  const figures = {};
  for (const n of [1, 2, 3]) {
    const value = ox(`hero.stat${n}`, t?.hero?.[`stat${n}`]);
    figures[`stat${n}`] = hasText(value) ? value : "";
    figures[`stat${n}Label`] = hasText(value) ? ox(`hero.stat${n}Label`, t?.hero?.[`stat${n}Label`]) : "";
  }

  const defaultContactEmail = tenant?.business_email || tenant?.owner_email || t?.contact?.info?.email;

  return {
    ...t,
    hero: {
      ...t?.hero,
      eyebrow: ox("hero.eyebrow", t?.hero?.eyebrow),
      title,
      titleLines: title.split(/\r?\n/).map((line) => line.trim()).filter(Boolean),
      script: optional("hero.script", t?.hero?.script),
      sub: optional("hero.sub", t?.hero?.sub),
      // BUG-210: these seven used to fall straight through the `...t?.hero` spread above,
      // with no `ox()` call reading their SITE_TEXT_OVERRIDES row back — a tenant's saved
      // override was written and never served. There is also no dictionary sample text to
      // fall back to any more (lib/content.js): the practitioner's title, city, region,
      // years-in-practice and hours-of-training are facts, not placeholder copy, so an
      // unset one resolves to "" and the renderer hides it (Hero.jsx / useSiteData.js).
      ...figures,
      credential: ox("hero.credential", t?.hero?.credential),
    },
    story: {
      ...t?.story,
      eyebrow: ox("story.eyebrow", t?.story?.eyebrow),
      title: ox("story.title", t?.story?.title),
      lead: ox("story.lead", t?.story?.lead),
      quote: ox("story.quote", t?.story?.quote),
      chapters: repeated(REPEATER_BY_KEY["story.chapters"], t, ox),
    },
    approach: {
      ...t?.approach,
      eyebrow: ox("approach.eyebrow", t?.approach?.eyebrow),
      title: ox("approach.title", t?.approach?.title),
      lead: ox("approach.lead", t?.approach?.lead),
      pillars: repeated(REPEATER_BY_KEY["approach.pillars"], t, ox),
    },
    services: { ...t?.services, lead: ox("services.lead", t?.services?.lead) },
    // BUG-210: footer.* used to fall through the `...t` spread at the bottom of this
    // object with no override applied at all — a saved `content_blocks` row for
    // "footer.tagline" was never read by anything. Same reasoning as the hero facts
    // above: a practice's specialty/areas of expertise is not sample copy, so
    // lib/content.js carries no default and an unset tagline resolves to "".
    footer: { ...t?.footer, tagline: ox("footer.tagline", t?.footer?.tagline) },
    contact: {
      ...t?.contact,
      lead: ox("contact.lead", t?.contact?.lead),
      info: {
        ...t?.contact?.info,
        email: ox("contact.email", defaultContactEmail),
        place: ox("contact.place", t?.contact?.info?.place),
      },
    },
    images: SITE_IMAGE_SLOTS.reduce(
      (acc, slot) => {
        // "site.heroBg" -> images.heroBg: the slot key IS the image's name in the copy.
        acc[slot.key.slice("site.".length)] = ox(slot.key, slot.fallback);
        return acc;
      },
      { ...ASSETS },
    ),
  };
}
